"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

// 카카오톡(및 인앱 브라우저) 탈출 — 멀티태스킹 불가 문제 해결.
// 알림톡/메시지 링크를 누르면 카톡 "인앱 브라우저"로 열려, 보는 동안 다른 채팅을 못 함.
// → 기기 기본 외부 브라우저(크롬/사파리)로 보내 카톡 종속에서 벗어나게 한다.
//
//  · 안드로이드 + 카톡 : kakaotalk://web/openExternal 로 "자동 전환" (OS/카톡이 지원)
//  · iOS / 그 외 인앱   : OS 정책상 자동 전환 불가 → "기본 브라우저로 열기" 안내 + 주소 복사
//
// 키오스크(/player)는 항상 인앱이 아닌 셸에서 뜨므로 제외.

const SUPPRESS_KEY = "kakao-escape-suppressed"; // 사용자가 "그냥 볼게요" 누르면 이 세션 동안만 숨김

type View = "hidden" | "android" | "ios";

export function KakaoBrowserEscape() {
  const t = useTranslations("components.kakaoEscape");
  const [view, setView] = useState<View>("hidden");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // 키오스크 디바이스는 제외
    if (window.location.pathname.startsWith("/player")) return;

    const ua = window.navigator.userAgent || "";
    const isKakao = /kakaotalk/i.test(ua);
    // 요청 범위 = 카카오톡 인앱 브라우저. 일반 크롬/사파리·그 외 인앱은 건드리지 않음.
    if (!isKakao) return;

    // iPhone/iPad 판별 (iPadOS 13+ 는 Mac 위장 → 터치포인트로 보강)
    const isIOS =
      /iphone|ipad|ipod/i.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // 이번 인앱 세션에서 이미 닫았으면 다시 안 띄움
    try {
      if (sessionStorage.getItem(SUPPRESS_KEY) === "1") return;
    } catch {
      /* sessionStorage 차단 환경 무시 */
    }

    const target = window.location.href;
    let timer: ReturnType<typeof setTimeout>;

    if (isIOS) {
      // ── 아이폰/아이패드: 자동 전환은 OS 정책상 불가 → "안내"만 (안내 카드는 iOS 전용) ──
      // effect 내 동기 setState 회피 위해 defer
      timer = setTimeout(() => setView("ios"), 0);
    } else {
      // ── 안드로이드 + 카톡: 외부 기본 브라우저로 자동 전환 ──────────────────
      // 카톡 전용 스킴. 성공하면 기본 브라우저로 튕겨나가고 이 페이지는 백그라운드로.
      window.location.href = "kakaotalk://web/openExternal?url=" + encodeURIComponent(target);
      // 구버전 카톡 등 자동전환이 안 될 때만 수동 버튼 폴백을 띄움 (안내 카드 아님)
      timer = setTimeout(() => setView("android"), 1500);
    }
    return () => clearTimeout(timer);
  }, []);

  if (view === "hidden") return null;

  const suppress = () => {
    try {
      sessionStorage.setItem(SUPPRESS_KEY, "1");
    } catch {
      /* 무시 */
    }
    setView("hidden");
  };

  const openExternal = () => {
    window.location.href =
      "kakaotalk://web/openExternal?url=" + encodeURIComponent(window.location.href);
  };

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // 인앱 웹뷰에서 clipboard API 가 막히면 임시 input 으로 폴백
      try {
        const el = document.createElement("textarea");
        el.value = url;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        setCopied(true);
      } catch {
        /* 복사 실패 — 사용자가 직접 주소창 복사 */
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,.82)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        animation: "teduFadeIn .2s ease",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          margin: "0 18px",
          borderRadius: 22,
          background: "#1c1c1e",
          border: "1px solid rgba(255,255,255,.08)",
          boxShadow: "0 20px 60px rgba(0,0,0,.6)",
          padding: "28px 22px 20px",
          color: "#fff",
          textAlign: "center",
          animation: "teduPop .28s cubic-bezier(.2,.8,.2,1)",
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- 오버레이 정적 아이콘, next/image 불필요 */}
        <img
          src="/icon-512.png"
          alt="easy-tedu"
          width={64}
          height={64}
          style={{ width: 64, height: 64, borderRadius: 15, margin: "0 auto 14px", display: "block" }}
        />

        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{t("title")}</div>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "rgba(235,235,245,.62)", margin: "0 0 20px" }}>
          {t.rich("desc", { br: () => <br /> })}
        </p>

        {view === "android" ? (
          <button onClick={openExternal} style={primaryBtn}>
            {t("openBtn")}
          </button>
        ) : (
          <>
            {/* iOS: 자동 전환 불가 → 메뉴 안내 + 주소 복사 */}
            <ol style={{ listStyle: "none", padding: 0, margin: "0 0 18px", textAlign: "left" }}>
              <GuideStep n={1}>
                {t.rich("step1", { b: (c) => <b>{c}</b> })}
              </GuideStep>
              <GuideStep n={2}>
                {t.rich("step2", { blue: (c) => <b style={{ color: "#0A84FF" }}>{c}</b> })}
              </GuideStep>
            </ol>
            <button onClick={copyLink} style={primaryBtn}>
              {copied ? t("copied") : t("copyBtn")}
            </button>
          </>
        )}

        <button onClick={suppress} style={textBtn}>
          {t("dismiss")}
        </button>
      </div>

      <style>{`
        @keyframes teduFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes teduPop { from { transform: scale(.96); opacity: 0 } to { transform: scale(1); opacity: 1 } }
      `}</style>
    </div>
  );
}

function GuideStep({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 0" }}>
      <span
        style={{
          flex: "0 0 24px",
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "#0A84FF",
          color: "#fff",
          fontSize: 13,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {n}
      </span>
      <span style={{ fontSize: 14, lineHeight: 1.45, color: "rgba(235,235,245,.9)" }}>{children}</span>
    </li>
  );
}

const primaryBtn: React.CSSProperties = {
  width: "100%",
  height: 50,
  borderRadius: 14,
  border: "none",
  background: "#0A84FF",
  color: "#fff",
  fontSize: 15.5,
  fontWeight: 600,
  cursor: "pointer",
};

const textBtn: React.CSSProperties = {
  marginTop: 12,
  background: "none",
  border: "none",
  color: "rgba(235,235,245,.5)",
  fontSize: 13,
  textDecoration: "underline",
  cursor: "pointer",
};
